// src/lib/planning/goalSupport.ts
//
// A goal can support the goal one rung above it: a month goal supports a
// season goal, a season goal supports a year goal. This module is the only
// place that reads those two links, so the pages stay presentational and the
// rules live in one testable spot — the same arrangement goalSteps.ts has for
// `goal_task_id`.
//
// Deliberately NOT goal_task_id. That says "is a step of", and a goal's open
// steps are carried along when the goal is kept forward. Support points UP,
// from child to parent, and no writer of a task ever touches it — so moving or
// carrying forward a task leaves every goal relationship exactly as it was.
//
//   month goal  → season goal   `supportsGoalTaskId` (a tasks row)
//   season goal → year goal     `goalId`             (a goals row)
//
// A link whose shape no longer holds — the parent is gone, or is not a goal,
// or is not the rung above — is IGNORED rather than drawn wrong. The stored
// value is left alone: it is a record of a decision, and a later repair can
// still read it.

import type { Task } from '@/types/task'
import type { Goal } from '@/types/goal'
import { seasonLabel, type Seasons } from '@/lib/cadence/seasons'

export type GoalRung = 'month' | 'season' | 'year'

export interface SupportLink {
  id: string
  title: string
  /** The rung the linked goal sits on — what the label in front of it means. */
  rung: GoalRung
  /** "Fall 2026", "October" — where to find it. Absent when it has no period
   *  of its own (a year goal is named by its year in `title`'s context). */
  period?: string
}

const isMonthGoal = (t: Task) => t.isGoal === true && t.bucket === 'month'
const isSeasonGoal = (t: Task) => t.isGoal === true && t.bucket === 'quarter'

const monthName = (d: Date | undefined) =>
  d ? d.toLocaleDateString('en-US', { month: 'long' }) : undefined

/**
 * The goal this one supports, or null.
 *
 * `tasks` and `goals` are the caller's own lists, which RLS has already
 * filtered — a parent the reader may not see is simply absent, so its title
 * cannot leak through a child that is shared. Same rule goalTitleMap follows.
 */
export function supportedGoal(
  goal: Task,
  tasks: readonly Task[],
  goals: readonly Goal[],
  seasons: Seasons,
): SupportLink | null {
  if (goal.isGoal !== true) return null

  if (isMonthGoal(goal)) {
    const parentId = goal.supportsGoalTaskId
    if (!parentId || parentId === goal.id) return null
    const parent = tasks.find((t) => t.id === parentId)
    if (!parent || !isSeasonGoal(parent)) return null
    return {
      id: parent.id,
      title: parent.title,
      rung: 'season',
      period: parent.seasonStart ? seasonLabel(parent.seasonStart, seasons) : undefined,
    }
  }

  if (isSeasonGoal(goal)) {
    const parent = goal.goalId ? goals.find((g) => g.id === goal.goalId) : undefined
    if (!parent || parent.status === 'archived') return null
    return { id: parent.id, title: parent.name, rung: 'year', period: String(parent.year) }
  }

  return null
}

/**
 * The goal a TASK is a step of (`goalTaskId`), as a support link — so a row
 * that already serves a goal can say which, instead of offering "Link to
 * goal" as though it served nothing (S3-08: a task under a Fall goal, seen on
 * October's list). Same privacy rule as above: read from the caller's
 * filtered list, so a goal the reader may not see is simply absent.
 */
export function goalOfTask(task: Task, tasks: readonly Task[], seasons: Seasons): SupportLink | null {
  if (task.isGoal === true || !task.goalTaskId) return null
  const goal = tasks.find((t) => t.id === task.goalTaskId)
  if (!goal || goal.isGoal !== true) return null
  if (isSeasonGoal(goal)) {
    return { id: goal.id, title: goal.title, rung: 'season', period: goal.seasonStart ? seasonLabel(goal.seasonStart, seasons) : undefined }
  }
  if (isMonthGoal(goal)) return { id: goal.id, title: goal.title, rung: 'month', period: monthName(goal.monthStart) }
  return null
}

/** The month goals that support this season goal, oldest first. */
export function monthGoalsSupporting(seasonGoalId: string, tasks: readonly Task[]): SupportLink[] {
  return tasks
    .filter((t) => isMonthGoal(t) && t.supportsGoalTaskId === seasonGoalId && t.id !== seasonGoalId)
    .sort(byCreation)
    .map((t) => ({ id: t.id, title: t.title, rung: 'month' as const, period: monthName(t.monthStart) }))
}

/** The season goals that support this year goal, oldest first. */
export function seasonGoalsSupporting(
  yearGoalId: string,
  tasks: readonly Task[],
  seasons: Seasons,
): SupportLink[] {
  return tasks
    .filter((t) => isSeasonGoal(t) && t.goalId === yearGoalId)
    .sort(byCreation)
    .map((t) => ({
      id: t.id,
      title: t.title,
      rung: 'season' as const,
      period: t.seasonStart ? seasonLabel(t.seasonStart, seasons) : undefined,
    }))
}

/** The goals one rung down that support this one, whichever rung it is on.
 *  A month goal is a leaf — nothing plans below it — so it gets nothing. */
export function goalsSupporting(goal: Task, tasks: readonly Task[]): SupportLink[] {
  if (isSeasonGoal(goal)) return monthGoalsSupporting(goal.id, tasks)
  return []
}

function byCreation(a: Task, b: Task) {
  return new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
}

export interface GoalChoice { id: string; title: string; period?: string }
export interface GoalChoiceGroup { label: string; goals: GoalChoice[] }

/**
 * The goals a task could be part of (`goal_task_id`), grouped by the period
 * they sit on — for task details' "Part of…" (walkthrough 2026-10-02, #26:
 * the panel offered "Make it a goal" but no way to say which goal a task
 * serves). Open goal TASKS only, never the task itself. `tasks` is the
 * caller's list, already narrowed to the life areas in view.
 */
export function goalChoices(task: Pick<Task, 'id'>, tasks: readonly Task[], seasons: Seasons): GoalChoiceGroup[] {
  const open = tasks.filter((t) => t.isGoal === true && !t.completed && t.id !== task.id)
  const byStart = (start: (t: Task) => Date | undefined) => (a: Task, b: Task) =>
    ((start(a)?.getTime() ?? 0) - (start(b)?.getTime() ?? 0)) || byCreation(a, b)
  const seasonGoals = open.filter(isSeasonGoal).sort(byStart((t) => t.seasonStart))
    .map((t) => ({ id: t.id, title: t.title, period: t.seasonStart ? seasonLabel(t.seasonStart, seasons) : undefined }))
  const monthGoals = open.filter(isMonthGoal).sort(byStart((t) => t.monthStart))
    .map((t) => ({ id: t.id, title: t.title, period: monthName(t.monthStart) }))
  const otherGoals = open.filter((t) => !isSeasonGoal(t) && !isMonthGoal(t)).sort(byCreation)
    .map((t) => ({ id: t.id, title: t.title }))
  return [
    { label: 'Season goals', goals: seasonGoals },
    { label: 'Month goals', goals: monthGoals },
    { label: 'Other goals', goals: otherGoals },
  ].filter((g) => g.goals.length > 0)
}
